import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  Users,
  Store,
  Clock,
  Globe,
  ClipboardCheck,
  CreditCard,
  RefreshCw,
  Inbox,
  Activity,
} from "lucide-react";
import { adminApi } from "../../../api/adminApi";
import { storeRequestApi } from "../../../api/storeRequestApi";
import { useNotifications } from "../../../context/NotificationContext";
import "./AdminDashboard.css";

const isValidDate = (date) => date && !Number.isNaN(new Date(date).getTime());

const formatDate = (date) => {
  if (!isValidDate(date)) return "Date unavailable";
  return new Date(date).toLocaleString("en-ZA", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatShortDate = (date) => {
  if (!isValidDate(date)) return "Date unavailable";
  return new Date(date).toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatRelative = (date) => {
  if (!isValidDate(date)) return "Date unavailable";
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return formatDate(date);
};

const labelOf = (status) => String(status || "pending").replace(/_/g, " ");

const initialsOf = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const plural = (count, one, many) => (count === 1 ? one : many);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { socket } = useNotifications();

  // `silent` refreshes keep the current content on screen instead of
  // swapping the whole page for a skeleton (used for live socket updates).
  const fetchDashboardData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);

      const [statsRes, activityRes, actionsRes, requestsRes] =
        await Promise.all([
          adminApi.getDashboardStats(),
          adminApi.getRecentActivity(10),
          adminApi.getActionRequired(),
          storeRequestApi.getAllRequests(),
        ]);

      setStats(statsRes.data);
      setActivity(activityRes.data || []);
      setActions(actionsRes.data);
      setRecentRequests(requestsRes.data || []);
      setLastUpdated(new Date());
      setError("");
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(
        err.response?.data?.error ||
          "We couldn't load the dashboard. Check your connection and try again.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleLiveUpdate = () => fetchDashboardData({ silent: true });
    socket.on("request:updated", handleLiveUpdate);
    socket.on("platform:updated", handleLiveUpdate);
    return () => {
      socket.off("request:updated", handleLiveUpdate);
      socket.off("platform:updated", handleLiveUpdate);
    };
  }, [fetchDashboardData, socket]);

  if (loading) {
    return (
      <div className="admin-dashboard-page" aria-busy="true" aria-live="polite">
        <div className="admin-skeleton admin-sk-title" />
        <div className="admin-skeleton admin-sk-stats" />
        <div className="admin-sk-columns">
          <div className="admin-skeleton admin-sk-panel" />
          <div className="admin-skeleton admin-sk-panel" />
        </div>
        <span className="admin-sr-only">Loading dashboard</span>
      </div>
    );
  }

  const statCards = [
    {
      label: "Total clients",
      value: stats?.TotalClients,
      Icon: Users,
      tone: "neutral",
      path: "/admin/clients",
    },
    {
      label: "Store requests",
      value: stats?.TotalRequests,
      Icon: Store,
      tone: "neutral",
      path: "/admin/requests",
    },
    {
      label: "Pending requests",
      value: stats?.PendingRequests,
      Icon: Clock,
      tone: "warning",
      path: "/admin/requests?status=pending",
    },
    {
      label: "Ready for review",
      value: stats?.ReadyForReview,
      Icon: ClipboardCheck,
      tone: "info",
      path: "/admin/requests?status=ready_for_review",
    },
    {
      label: "Live stores",
      value: stats?.LiveStores,
      Icon: Globe,
      tone: "success",
      path: "/admin/requests?status=live",
    },
    {
      label: "Active subscriptions",
      value: stats?.ActiveSubscriptions,
      Icon: CreditCard,
      tone: "neutral",
      path: "/admin/clients",
    },
  ];

  // Pipeline: how all requests are split across the main stages.
  const total = Number(stats?.TotalRequests) || 0;
  const pending = Number(stats?.PendingRequests) || 0;
  const review = Number(stats?.ReadyForReview) || 0;
  const live = Number(stats?.LiveStores) || 0;
  const other = Math.max(total - pending - review - live, 0);
  const pipeline = [
    { key: "pending", label: "Pending", value: pending },
    { key: "review", label: "Ready for review", value: review },
    { key: "live", label: "Live", value: live },
    { key: "other", label: "In progress / other", value: other },
  ];

  const attentionItems = actions
    ? [
        actions.NewRequests > 0 && {
          key: "new",
          count: actions.NewRequests,
          text: plural(
            actions.NewRequests,
            "new store request",
            "new store requests",
          ),
          button: "View",
          onClick: () => navigate("/admin/requests"),
        },
        actions.ReadyForReview > 0 && {
          key: "review",
          count: actions.ReadyForReview,
          text: plural(
            actions.ReadyForReview,
            "store awaiting review",
            "stores awaiting review",
          ),
          button: "Review",
          onClick: () => navigate("/admin/requests?status=ready_for_review"),
        },
        actions.UnreadMessages > 0 && {
          key: "messages",
          count: actions.UnreadMessages,
          text: plural(
            actions.UnreadMessages,
            "unread client message",
            "unread client messages",
          ),
          button: "View",
          onClick: () => navigate("/admin/requests"),
        },
        actions.PendingDomains > 0 && {
          key: "domains",
          count: actions.PendingDomains,
          text: plural(
            actions.PendingDomains,
            "domain needs action",
            "domains need action",
          ),
        },
      ].filter(Boolean)
    : [];

  const attentionTotal = attentionItems.reduce(
    (sum, item) => sum + Number(item.count),
    0,
  );

  const todayLabel = new Date().toLocaleDateString("en-ZA", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="admin-dashboard-page">
      <header className="admin-header">
        <div>
          <h1>Admin dashboard</h1>
          <p>
            {todayLabel}. Overview of clients, store requests and recent
            activity.
          </p>
        </div>
        <div className="admin-header-actions">
          {lastUpdated && (
            <span className="admin-updated">
              Updated{" "}
              {lastUpdated.toLocaleTimeString("en-ZA", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          <button
            type="button"
            className="admin-btn"
            onClick={() => fetchDashboardData({ silent: true })}
            disabled={refreshing}
          >
            <RefreshCw
              size={14}
              className={refreshing ? "admin-spin" : undefined}
              aria-hidden="true"
            />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>
      </header>

      {error && (
        <div className="admin-dashboard-error" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{error}</span>
          <button
            type="button"
            className="admin-link-btn"
            onClick={() => fetchDashboardData()}
          >
            Try again
          </button>
        </div>
      )}

      {attentionItems.length > 0 && (
        <section
          className="admin-attention"
          aria-labelledby="attention-heading"
        >
          <div className="admin-attention-head">
            <span className="admin-attention-icon">
              <AlertTriangle size={18} aria-hidden="true" />
            </span>
            <div>
              <h2 id="attention-heading">Needs your attention</h2>
              <p>
                {attentionTotal} {plural(attentionTotal, "item", "items")}{" "}
                waiting on you
              </p>
            </div>
          </div>
          <ul className="admin-attention-list">
            {attentionItems.map((item) => {
              const content = (
                <>
                  <span className="admin-attention-count">{item.count}</span>
                  <span className="admin-attention-text">{item.text}</span>
                  {item.onClick && (
                    <span className="admin-attention-cta">
                      {item.button}
                      <ChevronRight size={14} aria-hidden="true" />
                    </span>
                  )}
                </>
              );
              return (
                <li key={item.key}>
                  {item.onClick ? (
                    <button
                      type="button"
                      className="admin-attention-item"
                      onClick={item.onClick}
                    >
                      {content}
                    </button>
                  ) : (
                    <div className="admin-attention-item is-static">
                      {content}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="admin-stats" aria-label="Key figures">
        {statCards.map(({ label, value, Icon, tone, path }) => (
          <button
            className={`admin-stat admin-tone-${tone}`}
            key={label}
            type="button"
            onClick={() => navigate(path)}
            aria-label={`View ${label.toLowerCase()}`}
          >
            <span className="admin-stat-top">
              <span className="admin-stat-icon">
                <Icon size={16} aria-hidden="true" />
              </span>
              <ChevronRight
                className="admin-stat-arrow"
                size={16}
                aria-hidden="true"
              />
            </span>
            <span className="admin-stat-value">
              {(Number(value) || 0).toLocaleString("en-ZA")}
            </span>
            <span className="admin-stat-label">{label}</span>
          </button>
        ))}
      </section>

      {total > 0 && (
        <section
          className="admin-card admin-pipeline"
          aria-labelledby="pipeline-heading"
        >
          <div className="admin-card-heading">
            <div>
              <h2 id="pipeline-heading">Request pipeline</h2>
              <p>
                How all {total.toLocaleString("en-ZA")} store{" "}
                {plural(total, "request", "requests")} are distributed
              </p>
            </div>
          </div>
          <div
            className="admin-pipeline-bar"
            role="img"
            aria-label={pipeline
              .map((p) => `${p.label}: ${p.value}`)
              .join(", ")}
          >
            {pipeline
              .filter((p) => p.value > 0)
              .map((p) => (
                <span
                  key={p.key}
                  className={`admin-pipeline-seg seg-${p.key}`}
                  style={{ flexGrow: p.value }}
                  title={`${p.label}: ${p.value}`}
                />
              ))}
          </div>
          <ul className="admin-pipeline-legend">
            {pipeline.map((p) => (
              <li key={p.key}>
                <span className={`admin-legend-dot seg-${p.key}`} />
                <span className="admin-legend-label">{p.label}</span>
                <strong>{p.value.toLocaleString("en-ZA")}</strong>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="admin-columns">
        <section
          className="admin-card admin-recent-requests"
          aria-labelledby="requests-heading"
        >
          <div className="admin-card-heading">
            <div>
              <h2 id="requests-heading">Recent store requests</h2>
              <p>Latest client submissions and their status</p>
            </div>
            <button
              type="button"
              className="admin-btn"
              onClick={() => navigate("/admin/requests")}
            >
              View all
            </button>
          </div>

          {recentRequests.length === 0 ? (
            <div className="admin-empty">
              <Inbox size={22} aria-hidden="true" />
              <p>No store requests yet.</p>
              <span>New submissions from clients will appear here.</span>
            </div>
          ) : (
            <>
              <div className="admin-table-head" aria-hidden="true">
                <span>Business</span>
                <span>Submitted</span>
                <span>Status</span>
                <span />
              </div>
              <ul className="admin-recent-requests-list">
                {recentRequests.slice(0, 5).map((request) => {
                  const status = String(
                    request.Status || "pending",
                  ).toLowerCase();
                  const submitted = request.SubmittedDate;
                  return (
                    <li key={request.StoreRequestID}>
                      <button
                        type="button"
                        className="admin-recent-request-row"
                        onClick={() =>
                          navigate(`/admin/requests/${request.StoreRequestID}`)
                        }
                      >
                        <span className="admin-recent-request-info">
                          <strong>
                            {request.ClientBusinessName ||
                              request.BusinessName ||
                              "Store request"}
                          </strong>
                          <span className="admin-request-meta">
                            <span>
                              {request.RequestNumber
                                ? `#${request.RequestNumber}`
                                : "No request number"}
                            </span>
                            <span>{request.PackageName || "No package"}</span>
                            <span className="admin-meta-date">
                              {formatDate(submitted)}
                            </span>
                          </span>
                        </span>
                        <span className="admin-request-date">
                          {formatShortDate(submitted)}
                        </span>
                        <span
                          className={`admin-request-status status-${status}`}
                        >
                          <span className="admin-dot" />
                          {labelOf(status)}
                        </span>
                        <ChevronRight
                          size={16}
                          className="admin-row-arrow"
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>

        <section
          className="admin-card admin-activity"
          aria-labelledby="activity-heading"
        >
          <div className="admin-card-heading">
            <div>
              <h2 id="activity-heading">Recent activity</h2>
              <p>
                Last {activity.length || 10}{" "}
                {plural(activity.length || 10, "event", "events")}
              </p>
            </div>
          </div>

          {activity.length === 0 ? (
            <div className="admin-empty">
              <Activity size={22} aria-hidden="true" />
              <p>No recent activity.</p>
              <span>Client and request events will show up here.</span>
            </div>
          ) : (
            <ul className="admin-activity-list">
              {activity.map((item, index) => (
                <li key={`${item.Date}-${item.User}-${index}`}>
                  <span className="admin-avatar" aria-hidden="true">
                    {initialsOf(item.User)}
                  </span>
                  <span className="admin-activity-text">
                    <strong>{item.User}</strong>
                    <span>{item.Type}</span>
                  </span>
                  <time
                    dateTime={
                      isValidDate(item.Date)
                        ? new Date(item.Date).toISOString()
                        : undefined
                    }
                    title={formatDate(item.Date)}
                  >
                    {formatRelative(item.Date)}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
